import {
    Activity,
    ArrowLeft,
    BookOpen,
    Bot,
    ChevronRight,
    Globe,
    Handshake,
    LayoutGrid,
    type LucideIcon,
    Mail,
    Palette,
    PenLine,
    PlayCircle,
    Plug,
    ScrollText,
    Send,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

import { SidebarSetupFooter } from '@/components/setup/SidebarSetupFooter';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
    SidebarContent,
    SidebarGroup,
    SidebarGroupContent,
    SidebarGroupLabel,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarMenuSub,
    SidebarMenuSubButton,
    SidebarMenuSubItem,
    SidebarRail,
    Sidebar as SidebarUI,
} from '@/components/ui/sidebar';
import { useBackofficeAuth } from '@/contexts/BackofficeAuthContext';
import { useResolvedConfig } from '@/hooks/queries/useConfigQueries';
import { useAgentsConfig } from '@/hooks/useAgentsConfig';
import { useKnowledgeLearning } from '@/hooks/useKnowledgeLearning';
import { getGlobalAgents } from '@/services/agents';
import { LearningMark, type NavItem, NavMenuItem, PinnedSection, prefetchRoute } from './Sidebar';

const websiteAgentItems: NavItem[] = [
    { title: 'Config', href: '/config-studio/website-agent', icon: Palette },
    {
        title: 'Playground',
        href: '/config-studio/website-agent/playground',
        icon: PlayCircle,
    },
];

// What AI assistants are told this client sells (IX-5245). Everyone sees it; staff enable it (IX-5910).
const roseForAgentsItem: NavItem = {
    title: 'Rose for Agents',
    href: '/config-studio/rose-for-agents',
    icon: Bot,
};

const agentRoutes: Record<string, string> = {
    'content-agent': '/config-studio/content-agent',
};

// The website agent has its own group; the ChatGPT agent lives in Rose for Agents (IX-5910).
const isOtherAgent = (id: string) => id !== 'web-agent' && id !== 'llm-agent';

const agentIcons: Record<string, LucideIcon> = {
    'nurturing-agent': Mail,
    'crm-agent': Handshake,
    'contact-agent': Send,
    'content-agent': PenLine,
};

const otherAgentItems: NavItem[] = getGlobalAgents()
    .filter((agent) => isOtherAgent(agent.id))
    .map((agent) => ({
        title: agent.name,
        href: agentRoutes[agent.id] ?? `/config-studio/${agent.id}`,
        icon: agentIcons[agent.id] ?? LayoutGrid,
    }));

const sharedItems: NavItem[] = [
    // Brand & tone is shared across every agent — the enhanced cross-schema
    // screen (identity name/logo/language + appearance color/font + behavior voice).
    { title: 'Brand & tone', href: '/config-studio/brand', icon: Palette },
    // Sales Guidelines controls how Rose handles pricing and competitors;
    // Data Sources controls the knowledge it can retrieve.
    {
        title: 'Sales Guidelines',
        href: '/config-studio/sales-guidelines',
        icon: ScrollText,
    },
    { title: 'Data Sources', href: '/config-studio/data-sources', icon: BookOpen },
    { title: 'Integrations', href: '/config-studio/integration', icon: Plug },
    // Health — "is my Rose working?" diagnostics; spans every agent.
    { title: 'Health', href: '/config-studio/health', icon: Activity },
];

function AgentNavGroup({
    title,
    icon: Icon,
    items,
    pathname,
    defaultOpen = false,
}: {
    title: string;
    icon: NavItem['icon'];
    items: NavItem[];
    pathname: string;
    defaultOpen?: boolean;
}) {
    const isActive = (href: string) => {
        if (href === '/config-studio/website-agent/playground') {
            return pathname === href || pathname === '/config-studio/website-agent/evaluations';
        }
        if (href === '/config-studio/website-agent') {
            return (
                (pathname === href || pathname.startsWith(`${href}/`)) &&
                pathname !== '/config-studio/website-agent/playground' &&
                pathname !== '/config-studio/website-agent/evaluations'
            );
        }
        return pathname === href || pathname.startsWith(`${href}/`);
    };
    const hasActiveItem = items.some((item) => isActive(item.href));

    return (
        <Collapsible defaultOpen={defaultOpen || hasActiveItem} className="group/agent-nav">
            <SidebarMenuItem>
                <CollapsibleTrigger asChild>
                    <SidebarMenuButton
                        type="button"
                        tooltip={title}
                        size="lg"
                        className="h-9 py-0 group-data-[collapsible=icon]:justify-center"
                    >
                        <Icon />
                        <span className="group-data-[collapsible=icon]:hidden">{title}</span>
                        <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/agent-nav:rotate-90 group-data-[collapsible=icon]:hidden" />
                    </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent>
                    <SidebarMenuSub>
                        {items.map((item) => (
                            <SidebarMenuSubItem key={item.href}>
                                <SidebarMenuSubButton
                                    asChild
                                    isActive={isActive(item.href)}
                                    className="relative data-[active=true]:bg-[var(--accent-faint)] data-[active=true]:font-medium data-[active=true]:text-[var(--accent-foreground)] data-[active=true]:before:absolute data-[active=true]:before:top-2 data-[active=true]:before:bottom-2 data-[active=true]:before:left-0 data-[active=true]:before:w-0.5 data-[active=true]:before:rounded-r data-[active=true]:before:bg-[var(--accent-foreground)] data-[active=true]:before:content-[''] data-[active=true]:[&_svg]:text-[var(--accent-foreground)]"
                                >
                                    <Link
                                        to={item.href}
                                        onMouseEnter={() => prefetchRoute(item.href)}
                                    >
                                        <item.icon />
                                        <span>{item.title}</span>
                                    </Link>
                                </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                        ))}
                    </SidebarMenuSub>
                </CollapsibleContent>
            </SidebarMenuItem>
        </Collapsible>
    );
}

/**
 * Config Studio sidebar — swaps in for the main Sidebar on studio routes
 * (see isStudioRoute in AppLayout). Mirrors the POC: a back-link to the
 * dashboard, agent-specific navigation, and the parts shared across every agent.
 */
export function StudioSidebar() {
    const { pathname } = useLocation();
    const { isStaff, showAdvanced } = useBackofficeAuth();
    const { data } = useResolvedConfig();
    const { learning } = useKnowledgeLearning();
    const { agents } = useAgentsConfig();
    // Staff always see the other agents (they enable them there); clients only once one is live.
    // GEO is switched on by the `geo` feature, not agents['content-agent'].enabled.
    const showOtherAgents =
        isStaff ||
        data?.resolver.isFeatureEnabled('geo') === true ||
        agents.some(
            (agent) => isOtherAgent(agent.id) && agent.enabled && agent.status !== 'coming_soon'
        );
    const isActive = (href: string) => pathname === href || pathname.startsWith(`${href}/`);
    const visibleSharedItems = sharedItems.filter(
        (item) =>
            (!item.staffOnly || isStaff) &&
            (item.href !== '/config-studio/health' || isStaff || showAdvanced)
    );
    return (
        <SidebarUI collapsible="icon" className="border-r border-border bg-white">
            <SidebarHeader className="p-0">
                <div className="flex h-16 items-center border-b border-border-subtle px-4 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0">
                    <Link
                        to="/"
                        className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-[var(--accent-foreground)]"
                    >
                        <ArrowLeft className="h-4 w-4 shrink-0" />
                        <span className="group-data-[collapsible=icon]:hidden">
                            Back to dashboard
                        </span>
                    </Link>
                </div>
            </SidebarHeader>

            <SidebarContent className="py-4">
                <SidebarGroup className="px-2 py-2">
                    <SidebarGroupContent>
                        <SidebarMenu className="gap-px">
                            <AgentNavGroup
                                title="Website"
                                icon={Globe}
                                items={websiteAgentItems}
                                pathname={pathname}
                                defaultOpen
                            />
                            <NavMenuItem
                                item={roseForAgentsItem}
                                isActive={isActive(roseForAgentsItem.href)}
                            />
                            {showOtherAgents && (
                                <AgentNavGroup
                                    title="Other agents"
                                    icon={LayoutGrid}
                                    items={otherAgentItems}
                                    pathname={pathname}
                                />
                            )}
                        </SidebarMenu>
                    </SidebarGroupContent>
                </SidebarGroup>

                <PinnedSection />

                <SidebarGroup className="px-2 py-2">
                    <SidebarGroupLabel className="mb-1.5 px-2 text-[10.5px] font-semibold uppercase tracking-[0.12em] text-foreground-subtle">
                        Shared across agents
                    </SidebarGroupLabel>
                    <SidebarGroupContent>
                        <SidebarMenu className="gap-px">
                            {visibleSharedItems.map((item) => (
                                <NavMenuItem
                                    key={item.href}
                                    item={item}
                                    isActive={isActive(item.href)}
                                    // Rose spins on Data Sources whenever it is refreshing
                                    // what it knows, wherever the user happens to be.
                                    badge={
                                        learning && item.href === '/config-studio/data-sources' ? (
                                            <LearningMark />
                                        ) : null
                                    }
                                />
                            ))}
                        </SidebarMenu>
                    </SidebarGroupContent>
                </SidebarGroup>

                {/* Day-1 setup progress — pinned above the footer; self-hides once complete */}
                <SidebarSetupFooter />
            </SidebarContent>

            <SidebarRail />
        </SidebarUI>
    );
}
